import { ApparelCategorySlug, ApparelCategorySlugMtm } from '@isaia/entity/apparel';
import { SetOptional } from 'type-fest';
import { createMoney, Money, normalizeMoneyAmount } from '@qwentes/money';

type PriceExtraChargeCurrency = 'EUR' | 'USD' | string;

type ApparelCategorySurcharge = Partial<Record<ApparelCategorySlugMtm, number>>;

const PRICE_EXTRA_CHARGE_BY_CATEGORY_SLUG: Record<PriceExtraChargeCurrency, ApparelCategorySurcharge> = {
  EUR: {
    [ApparelCategorySlug.Suit]: 1000,
    [ApparelCategorySlug.Jacket]: 625,
    [ApparelCategorySlug.Overcoat]: 625,
    [ApparelCategorySlug.Shirt]: 125,
    [ApparelCategorySlug.Vest]: 125,
    [ApparelCategorySlug.Trouser]: 375,
    [ApparelCategorySlug.Knitwear]: 125,
    [ApparelCategorySlug.Overshirt]: 125,
  },
  USD: {
    [ApparelCategorySlug.Suit]: 1400,
    [ApparelCategorySlug.Jacket]: 900,
    [ApparelCategorySlug.Overcoat]: 900,
    [ApparelCategorySlug.Shirt]: 200,
    [ApparelCategorySlug.Vest]: 200,
    [ApparelCategorySlug.Trouser]: 550,
    [ApparelCategorySlug.Knitwear]: 200,
    [ApparelCategorySlug.Overshirt]: 200,
  },
  GBP: {
    [ApparelCategorySlug.Suit]: 900,
    [ApparelCategorySlug.Jacket]: 565,
    [ApparelCategorySlug.Overcoat]: 565,
    [ApparelCategorySlug.Shirt]: 115,
    [ApparelCategorySlug.Vest]: 115,
    [ApparelCategorySlug.Trouser]: 340,
    [ApparelCategorySlug.Knitwear]: 115,
    [ApparelCategorySlug.Overshirt]: 115,
  },
  CAD: {
    [ApparelCategorySlug.Suit]: 1500,
    [ApparelCategorySlug.Jacket]: 940,
    [ApparelCategorySlug.Overcoat]: 940,
    [ApparelCategorySlug.Shirt]: 190,
    [ApparelCategorySlug.Vest]: 190,
    [ApparelCategorySlug.Trouser]: 565,
    [ApparelCategorySlug.Knitwear]: 190,
    [ApparelCategorySlug.Overshirt]: 190,
  },
  CHF: {
    [ApparelCategorySlug.Suit]: 1000,
    [ApparelCategorySlug.Jacket]: 625,
    [ApparelCategorySlug.Overcoat]: 625,
    [ApparelCategorySlug.Shirt]: 125,
    [ApparelCategorySlug.Vest]: 125,
    [ApparelCategorySlug.Trouser]: 375,
  },
  JPY: {
    [ApparelCategorySlug.Suit]: 146000,
    [ApparelCategorySlug.Jacket]: 92000,
    [ApparelCategorySlug.Overcoat]: 92000,
    [ApparelCategorySlug.Shirt]: 19000,
    [ApparelCategorySlug.Vest]: 19000,
    [ApparelCategorySlug.Trouser]: 55000,
    [ApparelCategorySlug.Knitwear]: 19000,
    [ApparelCategorySlug.Overshirt]: 19000,
  },
};

function normalizeExtraCharge(value: number, { currency, decimals }: SetOptional<Pick<Money, 'currency' | 'decimals'>, 'decimals'>) {
  const amount = normalizeMoneyAmount(value, currency, decimals);
  return createMoney({ amount, currency, decimals });
}

export function getExtraCharge(currency: PriceExtraChargeCurrency, byCategorySlug: ApparelCategorySlugMtm | string) {
  const amount = PRICE_EXTRA_CHARGE_BY_CATEGORY_SLUG[currency]?.[byCategorySlug as ApparelCategorySlugMtm] || 0;
  return normalizeExtraCharge(amount, { currency });
}

export type ExtraChargeStrategy = (
  currency: PriceExtraChargeCurrency,
  byCategorySlug: ApparelCategorySlugMtm | string,
) => {
  add(price: Money): Money;

  remove(price: Money): Money;
};
